import type { ReactNode } from 'react';

type Cell = string | number | null;
export type Row = Record<string, Cell>;

export type Column = {
  key: string;
  label: string;
  numeric?: boolean;
  /** Anything the cell needs beyond its value — a badge, a tooltip, an emphasis. */
  render?: (row: Row) => ReactNode;
  /** What the cell reads as, when `render` shows something other than the value. Search matches
   * this; sorting and filtering still use the value. */
  text?: (row: Row) => string;
};

export type Filters = Record<string, string[]>;
export type Sort = { key: string; descending: boolean };

export const MISSING = '—';

/** How a value reads on the page. One function for cells, filter choices and search, so what you
 * type is what you can see — and so a candidate that scored badly still reads apart from one that
 * reported no score at all. */
export const displayOf = (value: Cell | undefined): string => {
  if (value === undefined || value === null || value === '') return MISSING;
  if (typeof value === 'number') return Number.isInteger(value) ? String(value) : value.toFixed(3);
  return value;
};

/**
 * Columns worth filtering by: the ones naming a kind of thing rather than measuring one.
 *
 * Read from the data, because nothing here knows what a task's breakdown contains. Holding no
 * measurements is the only test — ticking values off a column of numbers is not how anyone narrows
 * by one. Values being all distinct, all identical or simply numerous disqualifies nothing: a
 * leaderboard's model column is one row per candidate and picking two to sit side by side is the
 * whole reason to filter it.
 */
export const filterableKeys = (keys: string[], rows: Row[]): string[] =>
  keys.filter(key => hasValues(rows, key) && rows.every(row => typeof row[key] !== 'number'));

/** Whether a column holds anything at all. Saying so in one place covers both the metric no
 * candidate reported and the column that holds a button rather than a value. */
export const hasValues = (rows: Row[], key: string): boolean =>
  rows.some(row => displayOf(row[key]) !== MISSING);

/** Every value a column takes, sorted, so the choices read the same way each time. */
export const valuesOf = (rows: Row[], key: string): string[] =>
  [...new Set(rows.map(row => displayOf(row[key])))].sort();

/** Rows matching every active filter. Ticking nothing means no opinion, which is what makes
 * clearing a filter the same gesture as never setting one. */
export const applyFilters = (rows: Row[], filters: Filters): Row[] =>
  rows.filter(row =>
    Object.entries(filters).every(
      ([key, chosen]) => chosen.length === 0 || chosen.includes(displayOf(row[key])),
    ),
  );

/** Ticking a value adds it, ticking it again takes it away. */
export const toggleChoice = (filters: Filters, key: string, value: string): Filters => {
  const chosen = filters[key] ?? [];
  return {
    ...filters,
    [key]: chosen.includes(value) ? chosen.filter(each => each !== value) : [...chosen, value],
  };
};

export const activeCount = (filters: Filters): number =>
  Object.values(filters).filter(chosen => chosen.length > 0).length;

/** Rows ordered by one column. Unsorted, they keep the order they arrived in. */
export const sortRows = (rows: Row[], sort: Sort | null): Row[] => {
  if (sort === null) return rows;

  const direction = sort.descending ? -1 : 1;
  return [...rows].sort((left, right) => {
    const a = left[sort.key];
    const b = right[sort.key];
    if (typeof a === 'number' && typeof b === 'number') return (a - b) * direction;
    // Missing sorts last whichever way you are looking, because it is absence rather than a value.
    if (a === null || a === undefined) return 1;
    if (b === null || b === undefined) return -1;
    return String(a).localeCompare(String(b)) * direction;
  });
};

/** Clicking the sorted column flips it; clicking another starts it descending, which is what you
 * want from a score and harmless on a name. */
export const nextSort = (current: Sort | null, key: string): Sort =>
  current && current.key === key
    ? { key, descending: !current.descending }
    : { key, descending: true };

/** Rows containing the typed text anywhere. Across every column rather than a chosen one: having to
 * say which column a model name lives in is a step that answers nothing. */
export const matchesSearch = (
  row: Row,
  shownAs: ((row: Row) => string)[],
  search: string,
): boolean => {
  const wanted = search.trim().toLowerCase();
  if (wanted === '') return true;
  return shownAs.some(text => text(row).toLowerCase().includes(wanted));
};
